import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  signal,
  WritableSignal,
} from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { InputTextareaModule } from "primeng/inputtextarea";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TagModule } from "primeng/tag";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";
import { ApiService, handlePublicApiError } from "src/services/api/api.service";
import { ToastService } from "src/services/toast/toast.service";

type ActionType =
  | "open"
  | "clearErrors"
  | "disable"
  | "enable"
  | "broken"
  | "unbroken"
  | null;

interface BoxData {
  uniqueId: number;
  awb?: string;
  isOpen: boolean;
  isClosed: boolean;
  isBroken: boolean;
  isFull: boolean;
  isReserved: boolean;
  isErrored: boolean;
  isDisabled: boolean;
}

@Component({
  templateUrl: "./cbox-box-history-dialog.component.html",
  styleUrl: "./cbox-box-history-dialog.component.scss",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TagModule,
    ToastModule,
    ButtonModule,
    CommonModule,
    TooltipModule,
    MatDialogModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    InputTextareaModule,
    FormsModule,
    DialogModule,
    InputTextModule,
  ],
  providers: [ConfirmationService],
})
export class CboxBoxHistoryDialogComponent {
  currentAction = signal<ActionType>(null);
  fetching = signal<"ERROR" | "LOADING" | "SUCCESS">("LOADING");
  performingAction = signal<boolean>(false);
  boxData = signal<BoxData | undefined>(undefined);
  actionReason = signal<string>("");
  canProceed = computed(
    () => this.securityKeywordControl() === this.SECURITY_KEYWORD()
  );

  securityKeywordControl = signal("");
  SECURITY_KEYWORD = signal("CONFIRM");

  showConfirmDialog: WritableSignal<boolean> = signal<boolean>(false);
  confirmDialogHeader = signal<string>("Confirmare");
  confirmDialogMessage = signal<string>("");

  constructor(
    private api: ApiService,
    private confirmationService: ConfirmationService,
    private toastService: ToastService,
    private dialogRef: MatDialogRef<CboxBoxHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      boxData: BoxData;
    }
  ) {
    this.boxData.set(this.data.boxData);
    this.fetchData();
  }

  fetchData() {
    this.fetching.set("LOADING");
    this.fetching.set("SUCCESS");
  }

  confirmAction(actionType: ActionType) {
    let message = "";
    let header = "Confirmare";

    switch (actionType) {
      case "open":
        message = "Sigur doriți să forțați deschiderea acestei cutii?";
        header = "Forțare deschidere";
        this.SECURITY_KEYWORD.set("CONFIRM DESCHIDEREA FORTATA");
        break;
        case "clearErrors":
          message = "Doriti sa stergeti erorile cutiei?";
          header = "Stergere erori";
          this.SECURITY_KEYWORD.set("CONFIRM STERGERE ERORI");
        break;
      case "enable":
        message = "Sigur doriti sa marcati cutia ca fara erori?";
        header = "Marcare cutie ca fara erori";
        this.SECURITY_KEYWORD.set("CONFIRM MARCARE FARA ERORI");
        break;
      case "broken":
        message = "Sigur doriți să marcați această ușă ca stricată?";
        header = "Marcare ca stricată";
        this.SECURITY_KEYWORD.set("CONFIRM MARCARE CA STRICATA");
        break;
      case "unbroken":
        message = "Sigur doriți să marcați această ușă ca reparată?";
        header = "Marcare ca reparată";
        this.SECURITY_KEYWORD.set("CONFIRM MARCARE CA REPARATA");
        break;
      case "disable":
        message = "Sigur doriți să marcați această cutie ca nefuncțională?";
        header = "Marcare ca nefuncțională";
        this.SECURITY_KEYWORD.set("CONFIRM MARCARE CA NEFUNCTIONALA");
        break;
    }

    // Reset the reason field when opening a new confirmation
    this.actionReason.set("");
    this.currentAction.set(actionType);

    // Set up custom dialog
    this.confirmDialogHeader.set(header);
    this.confirmDialogMessage.set(message);
    this.showConfirmDialog.set(true);
  }

  updateReason(reason: string) {
    this.actionReason.set(reason);
  }

  confirmActionWithReason() {
    this.showConfirmDialog.set(false);
    this.performingAction.set(true);

    const actionType = this.currentAction();
    switch (actionType) {
      case "disable":
        this.disableBoxStatus();
        break;
      case "enable":
        this.enableBoxStatus();
        break;
      case "clearErrors":
        this.clearBoxErrors();
        break;
      case "open":
        this.forceOpenBox();
        break;
      case "broken":
        this.markDoorAsBroken();
        break;
      case "unbroken":
        this.markDoorAsUnbroken();
        break;
    }
  }

  cancelAction() {
    this.showConfirmDialog.set(false);
    this.currentAction.set(null);
    this.actionReason.set("");
    this.securityKeywordControl.set("");
  }

  shouldShowReason(): boolean {
    return true;
  }

  private forceOpenBox(): void {
    if (this.securityKeywordControl() !== this.SECURITY_KEYWORD()) {
      this.toastService.showErrorToast(
        "Eroare",
        "Vă rugăm să completați cuvântul cheie pentru confirmare!"
      );
      return;
    }
    const uniqueBoxId = this.boxData()?.uniqueId;
    this.api
      .post<CBox_PublicSuccessResponse<{ success: boolean }>>(
        `backend/internal/locker/box/${uniqueBoxId}/force-open`,
        {
          note: this.actionReason(),
          uniqueBoxId: this.boxData()?.uniqueId,
        }
      )
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.toastService.showSuccessToast(
              "Confirmare",
              "Comanda a fost trimisa cu succes"
            );
          } else {
            this.toastService.showErrorToast(
              "Eroare",
              "A aparut o eroare la inregistrarea comenzii"
            );
          }
          this.dialogRef.close();
        },
        error: (err) => {
          handlePublicApiError(err, this.toastService);
          this.performingAction.set(false);
        },
      });
  }

  private clearBoxErrors() {
    const url = `backend/internal/locker/box/${
      this.boxData()?.uniqueId
    }/clear-errors`;
    const body = this.shouldShowReason()
      ? { note: this.actionReason() }
      : undefined;
    this.handleApiCall(url, body);
  }

  private enableBoxStatus() {
    const url = `backend/internal/locker/box/${
      this.boxData()?.uniqueId
    }/mark-fixed`;
    const body = this.shouldShowReason()
      ? { note: this.actionReason() }
      : undefined;
    this.handleApiCall(url, body);
  }

  private disableBoxStatus() {
    const url = `backend/internal/locker/box/${
      this.boxData()?.uniqueId
    }/mark-error`;
    const body = this.shouldShowReason()
      ? { note: this.actionReason() }
      : undefined;
    this.handleApiCall(url, body);
  }

  private markDoorAsBroken() {
    const url = `backend/internal/locker/box/${
      this.boxData()?.uniqueId
    }/mark-broken`;
    const body = this.shouldShowReason()
      ? { note: this.actionReason() }
      : undefined;
    this.handleApiCall(url, body);
  }

  private markDoorAsUnbroken() {
    const url = `backend/internal/locker/box/${
      this.boxData()?.uniqueId
    }/mark-unbroken`;
    const body = this.shouldShowReason()
      ? { note: this.actionReason() }
      : undefined;
    this.handleApiCall(url, body);
  }

  private handleApiCall(url: string, body: object | undefined) {
    this.api.post<CBox_PublicSuccessResponse<void>>(url, body).subscribe({
      next: (res) => {
        this.toastService.showSuccessToast(
          "Confirmare.",
          "Statusul usii a fost actualizat cu success."
        );
        this.dialogRef.close();
      },
      error: (err) => {
        handlePublicApiError(err, this.toastService);
        this.performingAction.set(false);
      },
    });
  }

  getBoxStatusLabel() {
    const data = this.boxData();
    if (!data) return "Necunoscut";
    if (data.isErrored) return "Stricată (cu Erori)";
    if (data.isOpen) return "Deschisă";
    if (data.isFull) return "Ocupată";
    if (data.isReserved) return "Rezervată";
    if (data.isDisabled) return "Dezactivată";
    return "Disponibilă";
  }

  getBoxStatusSeverity() {
    const data = this.boxData();
    if (!data) return "info";
    if (data.isErrored) return "danger";
    if (data.isOpen) return "success";
    if (data.isFull) return "info";
    if (data.isReserved) return "warning";
    if (data.isDisabled) return "danger";
    return "success";
  }
}
